import React, { ReactElement, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { StackAlignment } from './StackAlignment';
import { StackDirection } from './StackDirection';

export interface StackProps {
  children: ReactNode;
  direction?: StackDirection;
  alignment?: StackAlignment;
  className?: string;
  xGap?: number;
  yGap?: number;
}

/**
 * Allows for one-dimensional layout of child elements in a row or column.
 *
 * @param children Child elements to lay out.
 * @param direction Column or row orientation.
 * @param alignment Left-aligned or center-aligned.
 * @param className Extra styles.
 * @param xGap Tailwind spacing along x axis.
 * @param yGap Tailwind spacing along y axis.
 * @constructor
 */
export default function Stack({
  children,
  direction = StackDirection.Column,
  alignment = StackAlignment.Left,
  className,
  xGap = 2,
  yGap = 4,
}: StackProps): ReactElement {
  return (
    <div
      className={twMerge(
        className,
        'flex',
        `gap-x-${xGap}`,
        `gap-y-${yGap}`,
        direction === StackDirection.Row && 'flex-row flex-wrap items-center',
        direction === StackDirection.Row &&
          alignment === StackAlignment.Center &&
          'justify-center',
        direction === StackDirection.Column && 'flex-col',
        direction === StackDirection.Column &&
          alignment === StackAlignment.Left &&
          'items-start',
        direction === StackDirection.Column &&
          alignment === StackAlignment.Center &&
          'items-center'
      )}
    >
      {children}
    </div>
  );
}
